import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { hasSubscriberAccess } from "@/lib/subscription";
import { Card, Button, Loader } from "@/components/ui/Primitives";
import { Lock, ArrowRight, Check, ShieldCheck } from "lucide-react";

/**
 * Wraps a premium page. When the user has no active subscription,
 * renders an upgrade paywall instead of the children.
 *
 * Usage:
 *   <PaywallGuard feature="Compliance" benefits={["…", "…"]}>
 *     <ComplianceContent/>
 *   </PaywallGuard>
 */
export default function PaywallGuard({ feature, benefits = [], children, allowInvestor = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader/>;

  const active = hasSubscriberAccess(user);
  // Investors and tenants don't need the landlord subscription.
  const skip = user?.role === "admin" || user?.role === "tenant" || (allowInvestor && user?.role === "investor");
  if (active || skip) return children;

  return (
    <div>
      <div className="mb-6 text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700 flex items-center gap-2">
        <Lock className="w-3.5 h-3.5"/> Smart feature · subscription required
      </div>

      <Card className="p-0 overflow-hidden border border-emerald-200 bg-gradient-to-br from-emerald-50 via-sky-50 to-white text-slate-900 relative shadow-sm" data-testid={`paywall-${(feature||'feature').toLowerCase().replace(/\s+/g,'-')}`}>
        <div className="absolute inset-0 opacity-50 pointer-events-none" style={{
          background: "radial-gradient(600px 280px at 100% 0%, rgba(125,211,252,0.30), transparent 60%), radial-gradient(500px 280px at 0% 100%, rgba(167,139,250,0.20), transparent 60%)"
        }}/>
        <div className="relative p-8 md:p-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-[11px] font-bold tracking-[0.18em] uppercase text-emerald-700 shadow-sm">
            <ShieldCheck className="w-3.5 h-3.5"/> Premium
          </div>

          <h1 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight mt-5 max-w-2xl bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-500 bg-clip-text text-transparent">
            Unlock {feature || "this feature"}
          </h1>
          <p className="text-slate-600 mt-4 text-base md:text-lg max-w-xl leading-relaxed">
            {feature || "This feature"} is part of Central Rentals' smart feature suite, available to active subscribers.
          </p>

          {benefits.length > 0 && (
            <ul className="mt-7 space-y-2.5 max-w-xl">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-slate-700 text-sm">
                  <Check className="w-4 h-4 text-emerald-600 flex-none mt-0.5"/> <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/billing" data-testid="paywall-upgrade-button"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 shadow-md shadow-emerald-500/30 transition-colors">
              See plans & subscribe <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to="/dashboard" data-testid="paywall-back-link"
              className="inline-flex items-center px-4 py-3 rounded-md bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-semibold text-sm transition-colors">
              Back to dashboard
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
